import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import anime from 'animejs/lib/anime.es.js'

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const STRIPE_BASE = 'https://buy.stripe.com/4gM14ofaUgyM7zR4ILgnK00'
const MILESTONE_NUMBERS = [1, 10, 25, 50, 100, 200, 500, 1000, 2500, 5000, 10000]

function buildStripeUrl(email, userId) {
  const params = new URLSearchParams()
  if (email) params.set('prefilled_email', email)
  if (userId) params.set('client_reference_id', userId)
  const qs = params.toString()
  return qs ? `${STRIPE_BASE}?${qs}` : STRIPE_BASE
}

function relTime(iso) {
  if (!iso) return ''
  const diff = (Date.now() - new Date(iso).getTime()) / 1000
  if (diff < 0) return 'just now'
  if (diff < 60) return `${Math.floor(diff)}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

function fmtJoinDate(iso) {
  if (!iso) return ''
  return new Date(iso).toISOString().slice(0, 10)
}

export default function Dashboard({ session }) {
  const navigate = useNavigate()
  const [member, setMember] = useState(null)
  const [loading, setLoading] = useState(true)
  const [idea, setIdea] = useState('')
  const [handle, setHandle] = useState('')
  const [ideaSubmitted, setIdeaSubmitted] = useState(false)
  const [time, setTime] = useState('')
  const [milestones, setMilestones] = useState([])
  const [latestJoins, setLatestJoins] = useState([])
  const wallRef = useRef(null)
  const tickerRef = useRef(null)
  const wallAnimatedRef = useRef(false)
  const tickerAnimatedRef = useRef(false)
  const prevClaimedRef = useRef(new Set())
  const prevJoinsRef = useRef(new Set())

  useEffect(() => {
    const t = setInterval(() => setTime(new Date().toTimeString().slice(0, 8)), 1000)
    setTime(new Date().toTimeString().slice(0, 8))
    return () => clearInterval(t)
  }, [])

  useEffect(() => {
    if (session?.user) loadMember()
  }, [session])

  useEffect(() => {
    let cancelled = false
    const fetchWall = async () => {
      const [{ data: milestoneData }, { data: recentData }] = await Promise.all([
        supabase.rpc('milestone_members', { numbers: MILESTONE_NUMBERS }),
        supabase.rpc('recent_members', { limit_n: 5 }),
      ])
      if (cancelled) return
      if (Array.isArray(milestoneData)) setMilestones(milestoneData)
      if (Array.isArray(recentData)) setLatestJoins(recentData)
    }
    fetchWall()
    const interval = setInterval(fetchWall, 30000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [])

  // Wall stagger-in is driven by a CSS @keyframes (`plaqueIn`) with an inline
  // --plaque-delay per plaque. Previous waves tried anime.set / anime [from,to]
  // and both stranded plaques at opacity:0; CSS keyframes are browser-compositor
  // driven and can't get stuck the same way. prefers-reduced-motion handled in
  // CSS via @media query. This effect is now diagnostic-only — kept so we can
  // confirm the wall actually mounts with the expected plaque count.
  useEffect(() => {
    if (!wallRef.current) return
    const plaques = wallRef.current.querySelectorAll('.plaque')
    // eslint-disable-next-line no-console
    console.log('[wall] mounted — plaques in DOM:', plaques.length,
      '· prefers-reduced-motion:', prefersReducedMotion())
  }, [])

  // Celebratory pulse when a milestone is newly claimed between fetches.
  // Decoupled from the stagger-in so the two paths can't fight.
  useEffect(() => {
    if (!wallRef.current) return
    if (prefersReducedMotion()) {
      prevClaimedRef.current = new Set(milestones.map(m => m.member_number))
      return
    }
    const current = new Set(milestones.map(m => m.member_number))
    if (prevClaimedRef.current.size > 0) {
      const fresh = [...current].filter(n => !prevClaimedRef.current.has(n))
      fresh.forEach(n => {
        const el = wallRef.current.querySelector(`[data-milestone="${n}"]`)
        if (el) {
          anime({
            targets: el,
            scale: [1, 1.06, 1],
            boxShadow: [
              '0 0 12px rgba(255,0,80,0.12) inset',
              '0 0 40px rgba(255,0,80,0.7), 0 0 12px rgba(255,0,80,0.18) inset',
              '0 0 12px rgba(255,0,80,0.12) inset',
            ],
            duration: 700,
            easing: 'easeOutBack',
          })
        }
      })
    }
    prevClaimedRef.current = current
  }, [milestones])

  // Ticker stagger-in — runs once when the first non-empty data arrives so the
  // rows actually exist in the DOM. New entries on subsequent fetches slide in
  // from above. Array [from, to] syntax, no anime.set.
  useEffect(() => {
    if (!tickerRef.current) return
    if (latestJoins.length === 0) return
    const reduced = prefersReducedMotion()
    const current = new Set(latestJoins.map(j => j.member_number))

    if (!tickerAnimatedRef.current) {
      if (!reduced) {
        const rows = Array.from(tickerRef.current.querySelectorAll('.join-row'))
        if (rows.length === 0) return
        anime({
          targets: rows,
          opacity: [0, 1],
          translateY: [-8, 0],
          duration: 320,
          delay: anime.stagger(60),
          easing: 'easeOutQuad',
        })
      }
      tickerAnimatedRef.current = true
    } else if (!reduced) {
      const fresh = [...current].filter(n => !prevJoinsRef.current.has(n))
      fresh.forEach(n => {
        const el = tickerRef.current.querySelector(`[data-join="${n}"]`)
        if (el) {
          anime({
            targets: el,
            opacity: [0, 1],
            translateY: [-8, 0],
            duration: 320,
            easing: 'easeOutQuad',
          })
        }
      })
    }
    prevJoinsRef.current = current
  }, [latestJoins])

  const loadMember = async () => {
    const { data } = await supabase
      .from('members')
      .select('*')
      .eq('user_id', session.user.id)
      .single()
    setMember(data)
    setLoading(false)
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    navigate('/')
  }

  const submitIdea = async () => {
    if (!idea.trim()) return
    await supabase.from('ideas').insert([{ idea, tiktok_handle: handle, user_id: session.user.id }])
    setIdeaSubmitted(true)
  }

  // Poll for subscription after returning from Stripe payment
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('payment') === 'success') {
      let attempts = 0
      const poll = setInterval(async () => {
        attempts++
        const { data } = await supabase.from('members').select('*').eq('user_id', session.user.id).single()
        if (data?.is_subscribed) {
          setMember(data)
          clearInterval(poll)
          window.history.replaceState({}, '', '/dashboard')
        }
        if (attempts >= 10) clearInterval(poll)
      }, 2000)
      return () => clearInterval(poll)
    }
  }, [session])

  const apps = [
    { idx: '01', sym: 'DAILY_RIDDLE', desc: 'One riddle per day. Global leaderboard. Fastest time wins.', emoji: '🧩', status: 'live', route: '/app/riddle' },
    { idx: '02', sym: 'AI_ROAST_ME', desc: 'Enter your name. Get destroyed by AI. Share it everywhere.', emoji: '🔥', status: 'live', route: '/app/roast' },
    { idx: '03', sym: 'DARK_HISTORY', desc: 'Enter birthday. Get dark historical events. Unsettling guaranteed.', emoji: '👻', status: 'live', route: '/app/dark-history' },
    { idx: '04', sym: 'VILLAIN_ORIGIN', desc: 'Forge your villain identity. Custom origin story. Screenshot the dossier.', emoji: '🦹', status: 'live', route: '/app/villain' },
    { idx: '05', sym: 'ROGUES_GALLERY', desc: 'Every villain forged by every member. The vault.', emoji: '🗂️', status: 'live', route: '/app/rogues' },
  ]

  const isSubscribed = member?.is_subscribed

  return (
    <div style={{ background: '#000', minHeight: '100vh', color: '#ff0055', fontFamily: "'Share Tech Mono', monospace", overflowX: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Bebas+Neue&family=Space+Grotesk:wght@700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        .scanlines{position:fixed;inset:0;background:repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.08) 2px,rgba(0,0,0,0.08) 3px);pointer-events:none;z-index:100}
        .grid-bg{position:fixed;inset:0;background-image:linear-gradient(rgba(255,0,80,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,0,80,0.025) 1px,transparent 1px);background-size:28px 28px;pointer-events:none;z-index:0}
        .app-row:hover{background:rgba(255,0,80,0.04);padding-left:12px!important;cursor:pointer}
        .app-row:hover .app-sym{color:#ff0055!important}
        .app-row{transition:all 0.15s}
        .plaque{padding:14px 10px 12px;position:relative;display:flex;flex-direction:column;justify-content:space-between;min-height:96px;background:rgba(255,0,80,0.015);transition:background 0.2s;animation:plaqueIn 500ms cubic-bezier(0.2,0,0.2,1) var(--plaque-delay,0ms) backwards}
        @keyframes plaqueIn{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @media (prefers-reduced-motion: reduce){.plaque{animation:none}}
        .plaque-claimed{border:1px solid rgba(255,0,80,0.6);box-shadow:0 0 12px rgba(255,0,80,0.12) inset;transition:box-shadow 0.3s ease,background 0.2s ease}
        .plaque-claimed:hover{background:rgba(255,0,80,0.06);box-shadow:0 0 22px rgba(255,0,80,0.4),0 0 12px rgba(255,0,80,0.18) inset}
        .plaque-vacant{border:1px dotted rgba(255,0,80,0.3)}
        .plaque-mine{border-color:#ff0055!important;box-shadow:0 0 22px rgba(255,0,80,0.4),0 0 12px rgba(255,0,80,0.2) inset}
        .plaque-mine .mine-dot{position:absolute;top:8px;right:8px;width:6px;height:6px;border-radius:50%;background:#ff0055;box-shadow:0 0 8px #ff0055;animation:pulse 1.4s ease infinite}
        .signout-btn{background:none;border:1px solid rgba(255,0,80,0.3);color:rgba(255,0,80,0.7);font-family:'Share Tech Mono',monospace;font-size:10px;padding:8px 14px;cursor:pointer;letter-spacing:1px;transition:all 0.2s}
        .signout-btn:hover{border-color:#ff0055;color:#ff0055}
        .t-input{width:100%;background:transparent;border:none;border-bottom:1px solid rgba(255,0,80,0.2);color:#ff0055;font-family:'Share Tech Mono',monospace;font-size:12px;padding:10px 0;outline:none;transition:border-color 0.2s}
        .t-input:focus{border-color:#ff0055}
        .t-input::placeholder{color:rgba(255,0,80,0.3)}
        .t-textarea{width:100%;background:rgba(255,0,80,0.02);border:1px solid rgba(255,0,80,0.15);color:#ff0055;font-family:'Share Tech Mono',monospace;font-size:12px;padding:12px;outline:none;resize:none;margin-bottom:10px;line-height:1.6;transition:border-color 0.2s}
        .t-textarea:focus{border-color:rgba(255,0,80,0.5)}
        .t-textarea::placeholder{color:rgba(255,0,80,0.3)}
        .submit-btn{background:none;border:1px solid rgba(255,0,80,0.4);color:#ff0055;font-family:'Share Tech Mono',monospace;font-size:10px;padding:10px 16px;cursor:pointer;letter-spacing:1px;transition:all 0.2s;white-space:nowrap}
        .submit-btn:hover{background:#ff0055;color:#000}
        .buy-btn{width:100%;background:#ff0055;color:#000;border:none;padding:20px;font-family:'Share Tech Mono',monospace;font-size:14px;letter-spacing:2px;cursor:pointer;font-weight:700;transition:all 0.2s;position:relative;overflow:hidden;display:block;text-align:center;text-decoration:none}
        .buy-btn::before{content:'';position:absolute;top:0;left:-100%;width:100%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.15),transparent);animation:shimmer 3s ease infinite}
        .buy-btn:hover{background:#fff;box-shadow:0 0 40px rgba(255,0,80,0.5)}
        @keyframes shimmer{0%{left:-100%}100%{left:200%}}
        @keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.3;transform:scale(1.4)}}
        @keyframes rowIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        .locked-app{opacity:0.25;cursor:not-allowed!important;pointer-events:none}
        .lock-icon{font-size:14px;opacity:0.4}
      `}</style>

      <div className="scanlines" />
      <div className="grid-bg" />

      <div style={{ maxWidth: 520, margin: '0 auto', padding: '0 0 80px', position: 'relative', zIndex: 2 }}>

        {/* TOP BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid rgba(255,0,80,0.15)' }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: 3, color: 'rgba(255,0,80,0.8)' }}>2DOLLARSITES EXCHANGE</div>
            <div style={{ fontSize: 9, color: 'rgba(255,0,80,0.5)', letterSpacing: 1, marginTop: 2 }}>
              {isSubscribed ? 'MEMBER PORTAL · MARKET OPEN' : 'GUEST ACCESS · PAYMENT REQUIRED'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 10, color: 'rgba(255,0,80,0.55)', letterSpacing: 1 }}>{time}</span>
            <button type="button" className="signout-btn" onClick={handleSignOut}>SIGN_OUT</button>
          </div>
        </div>

        {/* MEMBER NUMBER HERO */}
        {member && (
          <div style={{ padding: '24px 20px', borderBottom: '1px solid rgba(255,0,80,0.08)', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', right: -10, top: -10, fontFamily: "'Bebas Neue', sans-serif", fontSize: 160, color: 'rgba(255,0,80,0.03)', letterSpacing: -8, lineHeight: 1, pointerEvents: 'none', userSelect: 'none' }}>
              #{member.member_number}
            </div>
            <div style={{ fontSize: 9, letterSpacing: 3, color: 'rgba(255,0,80,0.6)', marginBottom: 8 }}>// YOUR_MEMBER_ID</div>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 72, color: '#ff0055', letterSpacing: -3, lineHeight: 1, textShadow: '0 0 40px rgba(255,0,80,0.25)', marginBottom: 6 }}>
              #{member.member_number}
            </div>
            <div style={{ fontSize: 12, color: '#ff0055', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, marginBottom: 2 }}>
              {member.display_name || session.user.email}
            </div>
            <div style={{ fontSize: 10, color: 'rgba(255,0,80,0.5)', letterSpacing: 1 }}>
              {session.user.email}
            </div>
            <div style={{ height: 1, background: 'linear-gradient(90deg, #ff0055, transparent)', marginTop: 16 }} />
          </div>
        )}

        {/* ═══ THE WALL · FOUNDERS LEDGER ═══ */}
        <div style={{ padding: '20px 20px 8px', borderBottom: '1px solid rgba(255,0,80,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14, fontSize: 9, letterSpacing: 2 }}>
            <span style={{ color: 'rgba(255,0,80,0.75)' }}>// THE_WALL · FOUNDERS_LEDGER</span>
            <span style={{ color: 'rgba(255,0,80,0.5)' }}>{milestones.length}/{MILESTONE_NUMBERS.length} CLAIMED</span>
          </div>
          <div style={{ fontSize: 10, color: 'rgba(255,0,80,0.55)', marginBottom: 14, lineHeight: 1.5 }}>
            Permanent plaques. Whoever claims the slot keeps it forever. The wall only grows.
          </div>
          <div ref={wallRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
            {MILESTONE_NUMBERS.map((n, i) => {
              const claim = milestones.find(m => m.member_number === n)
              const isMine = member && member.member_number === n
              const cls = claim ? `plaque plaque-claimed${isMine ? ' plaque-mine' : ''}` : 'plaque plaque-vacant'
              return (
                <div key={n} className={cls} data-milestone={n} style={{ '--plaque-delay': `${i * 50}ms` }}>
                  {isMine && <div className="mine-dot" />}
                  <div>
                    <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: claim ? 30 : 26, color: claim ? '#ff0055' : 'rgba(255,0,80,0.35)', letterSpacing: -1, lineHeight: 1, textShadow: claim ? '0 0 12px rgba(255,0,80,0.35)' : 'none' }}>
                      #{n}
                    </div>
                  </div>
                  {claim ? (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 11, color: 'rgba(255,0,80,0.9)', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {claim.display_name || '—'}
                      </div>
                      <div style={{ fontSize: 8, color: 'rgba(255,0,80,0.45)', letterSpacing: 1 }}>
                        JOINED {fmtJoinDate(claim.created_at)}
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 9, color: 'rgba(255,0,80,0.3)', fontStyle: 'italic', letterSpacing: 1 }}>
                        VACANT — UNCLAIMED
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* LATEST JOINS · real data */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,0,80,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 9, letterSpacing: 2 }}>
            <span style={{ color: 'rgba(255,0,80,0.75)' }}>// LATEST_JOINS · LIVE</span>
            <span style={{ color: '#ff0055', display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#ff0055', animation: 'pulse 1.5s ease infinite' }} />LIVE
            </span>
          </div>
          <div ref={tickerRef}>
            {latestJoins.length === 0 ? (
              <div style={{ fontSize: 10, color: 'rgba(255,0,80,0.45)', textAlign: 'center', padding: '10px 0', letterSpacing: 1 }}>
                // AWAITING_FIRST_JOIN
              </div>
            ) : latestJoins.map((m, i) => (
              <div
                key={m.member_number}
                className="join-row"
                data-join={m.member_number}
                style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < latestJoins.length - 1 ? '1px solid rgba(255,0,80,0.04)' : 'none', fontSize: 10 }}
              >
                <span style={{ color: 'rgba(255,0,80,0.85)' }}>
                  Member <b style={{ color: '#ff0055' }}>#{m.member_number}</b> · <b>{m.display_name}</b>
                </span>
                <span style={{ fontSize: 9, color: 'rgba(255,0,80,0.45)' }}>{relTime(m.created_at)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ═══ PAYWALL — shown to non-subscribers ═══ */}
        {!loading && !isSubscribed && (
          <div style={{ padding: '24px 20px', borderBottom: '1px solid rgba(255,0,80,0.08)', animation: 'fadeUp 0.4s ease' }}>
            <div style={{ border: '1px solid rgba(255,0,80,0.2)', padding: '28px 24px', background: 'rgba(255,0,80,0.03)', position: 'relative', marginBottom: 16 }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg,transparent,#ff0055,transparent)', boxShadow: '0 0 8px rgba(255,0,80,0.3)' }} />

              {/* LOCK STATUS */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(255,0,80,0.4)', border: '1px solid rgba(255,0,80,0.6)' }} />
                <span style={{ fontSize: 9, color: 'rgba(255,0,80,0.6)', letterSpacing: 2 }}>ACCESS_LOCKED · PAYMENT REQUIRED</span>
              </div>

              {/* PRICE */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 80, color: '#ff0055', letterSpacing: -4, lineHeight: 0.85, textShadow: '0 0 40px rgba(255,0,80,0.3)' }}>
                  $2<span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: 'rgba(255,0,80,0.5)', letterSpacing: -1 }}>/mo</span>
                </div>
                <div style={{ fontSize: 10, color: 'rgba(255,0,80,0.6)', letterSpacing: 2, marginTop: 6 }}>
                  CANCEL ANYTIME · INSTANT ACCESS · NO CONTRACTS
                </div>
              </div>

              {/* WHAT YOU GET */}
              <div style={{ marginBottom: 24, borderLeft: '1px solid rgba(255,0,80,0.2)', paddingLeft: 14 }}>
                <div style={{ fontSize: 11, color: 'rgba(255,0,80,0.7)', lineHeight: 1.8 }}>
                  Unlock the full app library. New drops every week.<br />
                  Daily riddle leaderboard. AI roasts. Dark history.<br />
                  The library never stops growing.
                </div>
              </div>

              {/* BUY IN BUTTON */}
              <a
                className="buy-btn"
                href={buildStripeUrl(session?.user?.email, session?.user?.id)}
              >
                ▶ JOIN FOR $2 — FORGE YOUR MEMBERSHIP →
              </a>

              <div style={{ textAlign: 'center', marginTop: 12, fontSize: 9, color: 'rgba(255,0,80,0.4)', letterSpacing: 1 }}>
                NEXT MEMBER: #{member ? member.member_number + 1 : '—'} · JOIN BEFORE SOMEONE ELSE DOES
              </div>
            </div>

            {/* Waiting for webhook to confirm payment */}
            <div style={{ textAlign: 'center', marginTop: 8, fontSize: 9, color: 'rgba(255,0,80,0.25)', letterSpacing: 1 }}>
              After payment, access unlocks automatically within seconds.
            </div>
          </div>
        )}

        {/* APP LIBRARY */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,0,80,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14, fontSize: 9, letterSpacing: 2 }}>
            <span style={{ color: 'rgba(255,0,80,0.75)' }}>// YOUR_PORTFOLIO · APP LIBRARY</span>
            <span style={{ color: 'rgba(255,0,80,0.5)' }}>{isSubscribed ? '5 ACTIVE' : '🔒 LOCKED'}</span>
          </div>
          {apps.map((app, i) => (
            <div
              key={i}
              className={`app-row ${!isSubscribed && app.status === 'live' ? 'locked-app' : ''}`}
              style={{
                display: 'grid', gridTemplateColumns: '28px 1fr auto', gap: 10, alignItems: 'center',
                padding: '14px 8px',
                borderBottom: i < apps.length - 1 ? '1px solid rgba(255,0,80,0.05)' : 'none',
                opacity: app.status === 'soon' ? 0.35 : (!isSubscribed ? 0.3 : 1),
              }}
              onClick={() => isSubscribed && app.route && navigate(app.route)}
            >
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: 'rgba(255,0,80,0.2)', lineHeight: 1 }}>{app.idx}</div>
              <div>
                <div className="app-sym" style={{ fontSize: 14, fontWeight: 800, color: 'rgba(255,0,80,0.85)', fontFamily: "'Space Grotesk', sans-serif", marginBottom: 2 }}>{app.sym}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,0,80,0.55)', lineHeight: 1.4 }}>{app.desc}</div>
              </div>
              <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                {!isSubscribed && app.status === 'live'
                  ? <span className="lock-icon">🔒</span>
                  : app.status === 'live'
                    ? <div style={{ fontSize: 8, color: '#ff0055', letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 3 }}>
                        <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#ff0055', animation: 'pulse 1.5s ease infinite' }} />LIVE
                      </div>
                    : <div style={{ fontSize: 8, color: 'rgba(255,0,80,0.3)', letterSpacing: 1 }}>○ SOON</div>
                }
                <span style={{ fontSize: 18, opacity: 0.6 }}>{app.emoji}</span>
              </div>
            </div>
          ))}
        </div>

        {/* IDEA SUBMISSION — available to everyone */}
        <div style={{ padding: '20px' }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: 'rgba(255,0,80,0.75)', marginBottom: 4 }}>// SUBMIT_ORDER — COMMUNITY IDEAS</div>
          <div style={{ fontSize: 10, color: 'rgba(255,0,80,0.6)', marginBottom: 16, lineHeight: 1.6 }}>If we build it, your TikTok handle gets credited on the page. Forever.</div>
          {ideaSubmitted ? (
            <div style={{ fontSize: 12, color: '#ff0055', letterSpacing: 1, padding: '16px 0' }}>▶ ORDER PLACED — WE'LL REVIEW IT</div>
          ) : (
            <>
              <textarea className="t-textarea" rows={3} placeholder="// describe your idea..." value={idea} onChange={e => setIdea(e.target.value)} />
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="t-input" placeholder="// your @tiktok handle" value={handle} onChange={e => setHandle(e.target.value)} />
                <button className="submit-btn" onClick={submitIdea}>PLACE ORDER</button>
              </div>
            </>
          )}
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,0,80,0.08)', display: 'flex', justifyContent: 'space-between', fontSize: 8, color: 'rgba(255,0,80,0.35)', letterSpacing: 1.5 }}>
          <span>2DOLLARSITES.COM</span>
          <span>MARKET OPEN 24/7</span>
          <span>EST.2026</span>
        </div>

      </div>
    </div>
  )
}
